/**
 * Endpoints de **Logística → Notas de Venta** (`/api/logistica/notas-venta`).
 *
 * El listado va paginado en el servidor, con los mismos filtros del panel
 * anterior (fechas, cliente y monto mínimo) más el buscador libre.
 */

import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  EnlaceNotaVenta,
  NotasVentaFiltros,
  NotasVentaRespuesta,
} from './notas-venta.types';

export const notasVentaKeys = {
  lista: (filtros: NotasVentaFiltros) => ['logistica', 'notas-venta', filtros] as const,
  enlace: (notaId: number) => ['logistica', 'notas-venta', 'enlace', notaId] as const,
} as const;

/** Arma la cadena de consulta del listado. */
function cadenaDe(filtros: NotasVentaFiltros): string {
  const parametros = new URLSearchParams();

  if (filtros.desde) parametros.set('desde', filtros.desde);
  if (filtros.hasta) parametros.set('hasta', filtros.hasta);
  if (filtros.cliente) parametros.set('cliente', filtros.cliente);
  if (filtros.montoMin) parametros.set('monto_min', filtros.montoMin);
  if (filtros.search) parametros.set('search', filtros.search);
  if (filtros.page) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const notasVentaApi = {
  listar: (filtros: NotasVentaFiltros = {}, signal?: AbortSignal): Promise<NotasVentaRespuesta> =>
    apiClient.get<NotasVentaRespuesta>(`/api/logistica/notas-venta${cadenaDe(filtros)}`, {
      signal,
    }),

  /** Enlace firmado para enviar la nota al cliente por WhatsApp. */
  enlace: (notaId: number, signal?: AbortSignal): Promise<EnlaceNotaVenta> =>
    apiClient.get<EnlaceNotaVenta>(`/api/logistica/notas-venta/${notaId}/enlace`, { signal }),
};

/** Notas de venta del filtro pedido. */
export function useNotasVenta(filtros: NotasVentaFiltros = {}) {
  return useQuery({
    queryKey: notasVentaKeys.lista(filtros),
    queryFn: ({ signal }) => notasVentaApi.listar(filtros, signal),
  });
}

/** Enlace firmado de una nota (se pide sólo cuando se abre el diálogo). */
export function useEnlaceNotaVenta(notaId: number | null) {
  return useQuery({
    queryKey: notasVentaKeys.enlace(notaId ?? 0),
    queryFn: ({ signal }) => notasVentaApi.enlace(notaId ?? 0, signal),
    enabled: notaId !== null,
    staleTime: 5 * 60 * 1000,
  });
}
